import { createSelector } from 'reselect';

import type { ChecklistGroup } from 'modules/entity/subEntities/intervention/types';
import type { Checklists, ChecklistCategory } from 'types/Works';

import { calculateWithVAT } from 'utils/costs';
import { getChecklistsPrice } from 'works/utils';

import { CHECKLIST_TYPE } from 'modules/entity/constants';
import { isSales } from 'modules/entity/subEntities/intervention/selectors';
import { getVAT } from 'modules/entity/subEntities/intervention/selectors/general';
import { CHECKLIST_GROUP_STATUS } from 'modules/entity/subEntities/intervention/constants';

import { getCountryCode, getEntity } from 'modules/entity/selectors';

import { showVAT } from './general';

type DisplayedChecklistGroup = ChecklistGroup & { tag?: string; value?: number; highlighted: boolean };

// eslint-disable-next-line default-param-last
export const isChecklistGroupNotDisplayable = (checklists: Array<Checklists> = [], groupId: string): boolean =>
  checklists
    .filter(({ checklistGroupId }) => checklistGroupId === groupId)
    .every(({ type }) => type === CHECKLIST_TYPE.DISCOUNT);

export const getChecklistGroupsTabs = createSelector(
  getEntity,
  showVAT,
  getVAT,
  isSales,
  getCountryCode,
  ({ checklistGroups = [], checklists = [] }, withVAT, dealerVAT, isSalesMode) => {
    const vat = withVAT ? dealerVAT : 0;
    return checklistGroups.reduce<Array<DisplayedChecklistGroup>>((acc, group) => {
      const groupChecklists = checklists.filter((checklist) => checklist.checklistGroupId === group.id);
      let price;
      let items;
      if (isSalesMode) {
        const mainChecklist = groupChecklists.find(
          ({ type }) => type === CHECKLIST_TYPE.VEHICLE
        ) ?? groupChecklists.find(
          ({ type }) => type === CHECKLIST_TYPE.ACCESSORY
        );

        price = mainChecklist?.price;
        items = mainChecklist?.items ?? [];
      } else {
        price = getChecklistsPrice(groupChecklists, 0);
        items = groupChecklists.reduce<Array<ChecklistCategory>>((itemsAcc, checklist) => [...itemsAcc, ...checklist.items ?? []], []);
      }

      const isAnswered = group.status !== CHECKLIST_GROUP_STATUS.PENDING;
      const isAccepted = group.status === CHECKLIST_GROUP_STATUS.ACCEPTED;
      const value = calculateWithVAT(price, vat ?? null);
      const isHighlighted = items.flatMap(({ advices }) => advices).some(({ highlighted }) => highlighted);
      const isNotDisplayable = !isAnswered && isChecklistGroupNotDisplayable(groupChecklists, group.id);

      const checklistGroup = {
        ...group,
        ...(isAccepted && { tag: CHECKLIST_GROUP_STATUS.ACCEPTED }),
        ...(typeof value === 'number' && { value }),
        highlighted: isHighlighted,
      };

      if (isAccepted) {
        acc.unshift(checklistGroup);
      } else if (!isNotDisplayable) {
        acc.push(checklistGroup);
      }

      return acc;
    }, []);
  }
);

export const hasChecklistGroupsSelection = createSelector(getChecklistGroupsTabs, (tabs = []) => tabs.length > 1);
